import { supabase } from '../lib/supabase.js';
import { state } from '../lib/state.js';

const TABLE = 'browserless_monthly_automation';
const BUTTON_CLASS = 'd24-browserless-monthly-button';
const SUPPORTED = new Set([
  'a1-amberone-pl','a2-awsa-pl','a4-sam-pl',
  'myto-cz','emyto-sk','darsgo-si','asfinag-at','hugo-hu',
  'hac-hr','azm-hr','bina-istra-hr','autostrade-it','autoroutes-fr',
]);
const LABELS = {
  'a1-amberone-pl':'A1 AmberOne','a2-awsa-pl':'A2 Autostrada Wielkopolska','a4-sam-pl':'A4 Katowice–Kraków',
  'myto-cz':'MYTO CZ','emyto-sk':'eMyto Słowacja','darsgo-si':'DarsGo Słowenia',
  'asfinag-at':'ASFINAG Austria','hugo-hu':'HU-GO Węgry','hac-hr':'HAC Chorwacja',
  'azm-hr':'AZM Chorwacja','bina-istra-hr':'Bina-Istra Chorwacja',
  'autostrade-it':'Autostrade Italia','autoroutes-fr':'Autoroutes Francja',
};
const LIMITS = { 'myto-cz':5, 'emyto-sk':6, 'hugo-hu':10, 'autostrade-it':3, 'autoroutes-fr':5 };
const REQUIRED = {
  // Wjazd, wyjazd, roadGroup, roadCode i operator są danymi zapisanej sekcji linii.
  // Tutaj walidujemy wyłącznie parametry pojazdu / kalkulatora.
  'a1-amberone-pl':['vehicleCategory'],
  'a2-awsa-pl':['vehicleCategory'],
  'a4-sam-pl':['vehicleCategory'],
  'myto-cz':['vehicleCategory','baseAxles','actualAxles','emissionClass','co2Class'],
  'emyto-sk':['vehicleCategory','emissionClass','co2Class'],
  'darsgo-si':['entryCountry','exitCountry','axles','emissionClass','co2Class'],
  'asfinag-at':['axles','emissionClass','co2Class'],
  'hugo-hu':['vehicleType','category','emissionClass','axleWeight','grossWeight','height','width','length'],
  'hac-hr':['vehicleCategory'],
  // AZM ma w Browserless klasę III ustawioną na stałe.
  'azm-hr':[],
  'bina-istra-hr':['vehicleCategory'],
  'autostrade-it':['vehicleCategory'],
  'autoroutes-fr':['vehicleCategory'],
};

const MOTORWAY = new Set([
  'a1-amberone-pl','a2-awsa-pl','a4-sam-pl',
  'hac-hr','azm-hr','bina-istra-hr',
  'autostrade-it','autoroutes-fr',
]);

// Jedno źródło reguł dla przycisku AUTOMAT i panelu gotowości w Diagnostyce.
export const browserlessMonthlyRules = Object.freeze({
  supported:SUPPORTED,
  labels:LABELS,
  limits:LIMITS,
  required:REQUIRED,
  motorway:MOTORWAY,
});

const ACTIVE_TEXT = 'AUTOMAT';
const INACTIVE_TEXT = 'AUTOMAT';

let observer = null;
let timer = 0;
let modal = null;
let pending = null;
let syncRunning = false;
let syncAgain = false;
const rows = new Map();

function key(routeId, sectionOrder, calculatorKey) { return `${routeId}:${Number(sectionOrder)}:${calculatorKey}`; }
function setStatus(message, kind='neutral') { const el=document.getElementById('extensionStatus'); if(el){el.textContent=message;el.className=`status ${kind}`;} }
function sectionOrder(card) {
  const fromDataset=Number(card?.dataset?.sectionOrder||0);
  if(fromDataset>0)return fromDataset;
  const m=String(card.querySelector('h3')?.textContent||'').match(/^\s*(\d+)\s*\./);
  return m?Number(m[1]):null;
}
function sectionFor(order, calc) { return (state.currentRoute?.sections||[]).find(s=>Number(s?.order)===Number(order)&&String(s?.calculatorKey||'')===calc)||null; }

async function settingsFor(calc,section=null) {
  const routeNumber=String(state.currentRoute?.lineNumber||'').trim();
  if(!routeNumber||!state.organizationId)return null;
  const {data,error}=await supabase.from('line_calculator_settings').select('settings').eq('organization_id',state.organizationId).eq('route_number',routeNumber).eq('calculator_key',calc).maybeSingle();
  if(error)throw error;
  const saved=data?.settings||null;
  if(!saved||!section)return saved;
  const sectionKey=String(Number(section?.order||0));
  const sectionSaved=saved.sectionRoutes&&typeof saved.sectionRoutes==='object'
    ? (saved.sectionRoutes[sectionKey]||{})
    : {};
  return {...saved,...sectionSaved};
}


function normalizeHacValue(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase();
}

function inferHacRoadGroup(section, existingSettings = {}) {
  const points = Array.isArray(section?.points) ? section.points : [];
  const entryName = String(
    section?.tollEntryName
    || section?.entryName
    || points[0]?.name
    || existingSettings?.entryName
    || ''
  ).trim();
  const exitName = String(
    section?.tollExitName
    || section?.exitName
    || points.at(-1)?.name
    || existingSettings?.exitName
    || ''
  ).trim();

  const roadCode = String(section?.roadCode || existingSettings?.roadCode || '').toUpperCase();
  const description = normalizeHacValue([
    section?.displayName,
    section?.operatorName,
    roadCode,
  ].filter(Boolean).join(' '));

  // 1. Najpewniejsze źródło: kod/nazwa sekcji wykrytej przez route-service.
  if (description.includes('bregana')) return 'bregana';
  if (/\bA4\b/i.test(roadCode) || /\ba4\b/.test(description)) return 'a4';
  if (/\bA7\b/i.test(roadCode) || /\ba7\b/.test(description) || description.includes('rupa')) return 'rupa';
  if (/\bA5\b/i.test(roadCode) || /\ba5\b/.test(description)) return 'a3';
  if (
    /\bA1\b/i.test(roadCode)
    || /\bA6\b/i.test(roadCode)
    || /\bA10\b/i.test(roadCode)
    || /\bD425\b/i.test(roadCode)
    || /\ba1\b/.test(description)
    || /\ba6\b/.test(description)
    || /\ba10\b/.test(description)
    || /\bd425\b/.test(description)
  ) return 'a1';

  const entry = normalizeHacValue(entryName);
  const exit = normalizeHacValue(exitName);

  const inPair = set => set.has(entry) && set.has(exit);

  // A4 Zagreb–Goričan
  const a4 = new Set([
    'gorican','cakovec','varazdin','varazdinske toplice','varaz toplice',
    'novi marof','breznicki hum','brez hum','komin','sveta helena','sveta',
  ]);
  if (inPair(a4)) return 'a4';

  // A7 Rupa
  const rupa = new Set(['rupa','rijeka','krizisce']);
  if (inPair(rupa)) return 'rupa';

  // A3 Bregana ma osobny cennik.
  const bregana = new Set(['bregana','zagreb zapad']);
  if (inPair(bregana)) return 'bregana';

  // A3/A5 — wschodnia sieć HAC.
  const a3 = new Set([
    'zagreb istok','ivanic grad','kriz','popovaca','kutina','novska',
    'okucani','nova gradiska','slavonski brod zapad','slavonski brod istok',
    'zupanja','lipovac','svilaj','dakovo','cepin','osijek','beli manastir',
  ]);
  if (inPair(a3)) return 'a3';

  // A1/A6/A10/D425 — m.in. Zagreb → Zadar, Split i Rijeka/Grobnik.
  const a1 = new Set([
    'zagreb','zdencina','jastrebarsko','karlovac','novigrad','bosiljevo',
    'vrbovsko','ravna gora','delnice','vrata','ostrovica',
    'rijeka','rijeka grobnik',
    'ogulin','zuta lokva','brinje','otocac','perusic','gospic',
    'gornja ploca','sveti rok','rovanjska','posedarje',
    'zadar','zadar istok','zadar centar',
    'benkovac','vodice','skradin','sibenik','podi','vucevica',
    'prgomet','danilo','bisko','split','blato na cetini','sestanovac',
    'zagvozd','vrgorac','ravca','carapine','karamatici',
  ]);
  if (inPair(a1)) return 'a1';

  // A3 jest niejednoznaczne przy Breganie, dlatego dopiero po sprawdzeniu stacji.
  if (/\bA3\b/i.test(roadCode) || /\ba3\b/.test(description)) return 'a3';

  // 2. Zapisana wartość jest fallbackiem, a nie źródłem ważniejszym od aktualnej sekcji.
  const supplied = String(section?.roadGroup || existingSettings?.roadGroup || '').trim().toLowerCase();
  return ['a1','a3','a4','bregana','rupa'].includes(supplied) ? supplied : '';
}

function motorwayRouteData(section, calc='', existingSettings={}) {
  const points=Array.isArray(section?.points)?section.points:[];
  const sectionKey=String(Number(section?.order||0));
  const savedSectionRoute=(existingSettings?.sectionRoutes&&typeof existingSettings.sectionRoutes==='object')
    ? (existingSettings.sectionRoutes[sectionKey]||{})
    : {};
  const source={...existingSettings,...savedSectionRoute};

  return {
    entryName:String(source?.entryName||section?.tollEntryName||section?.entryName||points[0]?.name||'').trim(),
    exitName:String(source?.exitName||section?.tollExitName||section?.exitName||points.at(-1)?.name||'').trim(),
    roadGroup:String(calc==='hac-hr'
      ? inferHacRoadGroup(section,source)
      : (section?.roadGroup||source?.roadGroup||'')
    ).trim(),
    roadCode:String(section?.roadCode||source?.roadCode||'').trim(),
    operatorName:String(section?.operatorName||source?.operatorName||'').trim(),
    displayName:String(section?.displayName||source?.displayName||'').trim(),
    sectionOrder:Number(section?.order||source?.sectionOrder||0),
  };
}

function validateSection(section, calc) {
  const limit=LIMITS[calc];
  if(limit && (section?.points||[]).length>limit) {
    return `${LABELS[calc]||calc}: zapisany odcinek ma ${(section?.points||[]).length} punktów, a automat obsługuje maksymalnie ${limit}.`;
  }
  // Nie blokujemy przycisku autostrady na podstawie samego obiektu sekcji.
  // Starsze zapisane linie mogą mieć roadGroup/wjazd/wyjazd w
  // line_calculator_settings, mimo że bieżący snapshot sekcji ich nie zawiera.
  return '';
}

function isMytoBorderPointForMonthly(point){
  return Boolean(
    point?.isBorder
    || point?.pointType==='border'
    || String(point?.countryCode||'').includes('/')
    || /^granica\b/i.test(String(point?.name||'').trim())
  );
}

async function validateSettings(calc,section=null) {
  const required=[...(REQUIRED[calc]||[])];
  const settings=await settingsFor(calc,section);

  if(calc==='myto-cz'){
    const points=Array.isArray(section?.points)?section.points:[];
    if(isMytoBorderPointForMonthly(points[0]))required.push('entryBorderValue');
    if(isMytoBorderPointForMonthly(points.at(-1)))required.push('exitBorderValue');
  }

  if(!required.length)return '';
  if(!settings)return `Najpierw ustaw parametry dla ${LABELS[calc]||calc}.`;

  const missing=Array.from(new Set(required)).filter(name=>String(settings?.[name]??'').trim()==='');
  if(!missing.length)return '';

  if(calc==='myto-cz'&&missing.some(name=>name==='entryBorderValue'||name==='exitBorderValue')){
    return 'MYTO CZ: przed włączeniem automatu wybierz i zapisz dokładne przejście WJAZD/WYJAZD z listy MYTO CZ.';
  }
  return `Brak zapisanych parametrów pojazdu ${LABELS[calc]||calc}: ${missing.join(', ')}.`;
}

async function persistMotorwayRouteData(calc, section) {
  if(!MOTORWAY.has(calc))return await settingsFor(calc);
  const routeNumber=String(state.currentRoute?.lineNumber||'').trim();
  if(!routeNumber||!state.organizationId)return null;

  const current=await settingsFor(calc)||{};
  const route=motorwayRouteData(section,calc,current);

  // Puste wartości z bieżącego snapshotu NIE MOGĄ kasować danych zapisanych
  // wcześniej dla tej konkretnej linii.
  const nonEmptyRoute=Object.fromEntries(
    Object.entries(route).filter(([name,value]) => (
      name==='sectionOrder'
        ? Number(value)>0
        : String(value??'').trim()!==''
    ))
  );

  const sectionKey=String(Number(section?.order||0));
  const previousSectionRoute=
    current.sectionRoutes&&typeof current.sectionRoutes==='object'
      ? (current.sectionRoutes[sectionKey]||{})
      : {};

  const persistentSectionRoute={
    ...nonEmptyRoute,
    entryName:String(previousSectionRoute.entryName||nonEmptyRoute.entryName||'').trim(),
    exitName:String(previousSectionRoute.exitName||nonEmptyRoute.exitName||'').trim(),
    roadGroup:String(previousSectionRoute.roadGroup||nonEmptyRoute.roadGroup||'').trim(),
    roadCode:String(previousSectionRoute.roadCode||nonEmptyRoute.roadCode||'').trim(),
    operatorName:String(previousSectionRoute.operatorName||nonEmptyRoute.operatorName||'').trim(),
  };

  const settings={
    ...current,
    sectionRoutes:{
      ...(current.sectionRoutes&&typeof current.sectionRoutes==='object'?current.sectionRoutes:{}),
      [sectionKey]:persistentSectionRoute,
    },
  };

  // Dla jednej sekcji zachowujemy top-level dla zgodności ze starszymi jobami.
  const sameCalcSections=(state.currentRoute?.sections||[]).filter(item=>String(item?.calculatorKey||'')===calc);
  if(sameCalcSections.length===1)Object.assign(settings,persistentSectionRoute);

  if(calc==='azm-hr')settings.vehicleCategory=current.vehicleCategory||settings.vehicleCategory||'III';

  const {error}=await supabase.from('line_calculator_settings').upsert({
    organization_id:state.organizationId,
    route_number:routeNumber,
    calculator_key:calc,
    settings,
    updated_at:new Date().toISOString(),
  },{onConflict:'organization_id,route_number,calculator_key'});
  if(error)throw error;
  return settings;
}

async function validateMotorwayRouteSettings(calc, section) {
  if(!MOTORWAY.has(calc))return '';

  // Najpierw scal dane zapisanej sekcji z już istniejącą konfiguracją tej linii.
  const settings=await persistMotorwayRouteData(calc,section) || {};
  const route=motorwayRouteData(section,calc,settings);

  const entryName=String(route.entryName||settings.entryName||'').trim();
  const exitName=String(route.exitName||settings.exitName||'').trim();
  const roadGroup=String(route.roadGroup||settings.roadGroup||'').trim();

  if(!entryName||!exitName){
    return `${LABELS[calc]||calc}: brak zapisanego punktu wjazdu lub wyjazdu dla tej linii. Uruchom kalkulator ręcznie albo zapisz linię ponownie.`;
  }

  if(calc==='hac-hr'&&!roadGroup){
    return 'HAC Chorwacja: nie udało się rozpoznać grupy autostrady z zapisanej sekcji tej linii. Ponownie wyznacz trasę i zapisz linię.';
  }

  return '';
}

function ensureModal() {
  if(modal)return modal;
  const style=document.createElement('style');
  style.textContent=`
    #d24MonthlyBrowserless{position:fixed;inset:0;z-index:12000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(15,23,42,.62)}
    #d24MonthlyBrowserless.hidden{display:none} #d24MonthlyBrowserless .box{width:min(620px,100%);background:#fff;border-radius:16px;padding:24px;box-shadow:0 24px 70px rgba(0,0,0,.32);color:#172033}
    #d24MonthlyBrowserless h2{margin:0 0 8px;font-size:23px} #d24MonthlyBrowserless .route{padding:10px 12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:9px;margin:10px 0 16px;line-height:1.45}
    #d24MonthlyBrowserless label{display:flex;flex-direction:column;gap:6px;font-weight:700;margin-top:12px} #d24MonthlyBrowserless select,#d24MonthlyBrowserless input{min-height:42px;padding:8px 10px;border:1px solid #cbd5e1;border-radius:8px;font:inherit}
    #d24MonthlyBrowserless .note{margin-top:12px;font-size:12px;color:#475467;line-height:1.45} #d24MonthlyBrowserless .actions{display:flex;gap:10px;justify-content:flex-end;margin-top:20px;flex-wrap:wrap}
    #d24MonthlyBrowserless button{border:0;border-radius:8px;padding:10px 15px;font-weight:700;cursor:pointer} #d24MonthlyBrowserless .save{background:#17663a;color:#fff} #d24MonthlyBrowserless .disable{background:#b42318;color:#fff} #d24MonthlyBrowserless .cancel{background:#e8edf3;color:#26364a}
  `;
  document.head.appendChild(style);
  const root=document.createElement('div'); root.id='d24MonthlyBrowserless'; root.className='hidden'; root.innerHTML=`
    <section class="box" role="dialog" aria-modal="true"><h2>Automatyka miesięczna Browserless</h2><div class="route"></div>
      <label>Termin w miesiącu<select class="mode"><option value="last_day">Ostatni dzień miesiąca</option><option value="day_of_month">Konkretny dzień — wybierz z kalendarza</option></select></label>
      <label class="date-row hidden">Wybierz datę<input class="date" type="date"></label>
      <div class="note">Dla konkretnej daty zapisywany jest dzień miesiąca. Jeśli wybierzesz np. 31., a miesiąc ma mniej dni, zadanie trafi do kolejki ostatniego dnia tego miesiąca. Wszystkie zadania są wykonywane kolejno — globalnie tylko 1 Browserless naraz.</div>
      <div class="actions"><button type="button" class="disable">Wyłącz automat</button><button type="button" class="cancel">Anuluj</button><button type="button" class="save">Zapisz automat</button></div>
    </section>`;
  document.body.appendChild(root);
  const api={root,route:root.querySelector('.route'),mode:root.querySelector('.mode'),dateRow:root.querySelector('.date-row'),date:root.querySelector('.date'),save:root.querySelector('.save'),disable:root.querySelector('.disable'),cancel:root.querySelector('.cancel')};
  api.mode.addEventListener('change',()=>api.dateRow.classList.toggle('hidden',api.mode.value!=='day_of_month'));
  api.cancel.addEventListener('click',closeModal); root.addEventListener('click',e=>{if(e.target===root)closeModal();});
  api.save.addEventListener('click',saveModal); api.disable.addEventListener('click',disableModal); modal=api; return api;
}

function closeModal(){ if(!modal)return; modal.root.classList.add('hidden'); pending=null; document.body.style.overflow=''; }
function dateForDay(day){ const now=new Date(); const y=now.getFullYear(); const m=now.getMonth(); const last=new Date(y,m+1,0).getDate(); const d=Math.min(Math.max(1,Number(day)||now.getDate()),last); return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`; }

async function openModal(section, calc, order) {
  if(!state.currentRoute?.id)return setStatus('Najpierw zapisz linię.','warning');
  const limitError=validateSection(section,calc); if(limitError){window.alert(limitError);return;}

  const routeError=await validateMotorwayRouteSettings(calc,section);
  if(routeError)return setStatus(routeError,'warning');

  const saved=automationRowForSection(state.currentRoute.id,order,calc);
  if(!saved?.enabled){ const settingsError=await validateSettings(calc,section); if(settingsError)return setStatus(settingsError,'warning'); }
  const ui=ensureModal(); pending={section,calc,order,rowSectionOrder:saved?Number(saved.section_order):Number(order)}; ui.route.innerHTML=`<strong>${LABELS[calc]||calc}</strong><br>${(section.points||[]).map(p=>String(p.name||'')).join(' → ')}`;
  ui.mode.value=saved?.schedule_mode||'last_day'; ui.date.value=dateForDay(saved?.schedule_day||new Date().getDate()); ui.dateRow.classList.toggle('hidden',ui.mode.value!=='day_of_month'); ui.disable.style.display=saved?.enabled?'inline-block':'none'; ui.root.classList.remove('hidden'); document.body.style.overflow='hidden';
}

async function saveModal(){
  if(!pending||!modal)return; const {calc,order,section,rowSectionOrder}=pending; const routeId=state.currentRoute?.id; if(!routeId)return;
  const mode=modal.mode.value; let day=null; if(mode==='day_of_month'){ if(!modal.date.value)return setStatus('Wybierz datę z kalendarza.','warning'); day=Number(modal.date.value.slice(-2)); }
  const sectionError=validateSection(section,calc); if(sectionError)return setStatus(sectionError,'warning');
  const routeError=await validateMotorwayRouteSettings(calc,section); if(routeError)return setStatus(routeError,'warning');
  const settingsError=await validateSettings(calc,section); if(settingsError)return setStatus(settingsError,'warning');
  modal.save.disabled=true;

  const targetOrder=Number(order);
  const previousOrder=Number(rowSectionOrder||targetOrder);
  const updatePayload={section_order:targetOrder,enabled:true,schedule_mode:mode,schedule_day:day,next_run_on:null,last_error:''};
  let data=null;
  let error=null;

  // Jeżeli automat wskazuje starą numerację sekcji, najpierw przenosimy ten
  // sam rekord na aktualny section_order. Zachowuje to historię harmonogramu
  // i nie tworzy drugiego aktywnego automatu dla tego samego kalkulatora.
  if(previousOrder!==targetOrder){
    const response=await supabase.from(TABLE)
      .update(updatePayload)
      .eq('organization_id',state.organizationId)
      .eq('route_id',routeId)
      .eq('section_order',previousOrder)
      .eq('calculator_key',calc)
      .select('*')
      .maybeSingle();
    data=response.data; error=response.error;
  }

  if(!data&&!error){
    const response=await supabase.from(TABLE).upsert({organization_id:state.organizationId,route_id:routeId,section_order:targetOrder,calculator_key:calc,...updatePayload},{onConflict:'organization_id,route_id,section_order,calculator_key'}).select('*').single();
    data=response.data; error=response.error;
  }

  modal.save.disabled=false;
  if(error)return setStatus(`Nie udało się zapisać automatu: ${error.message}`,'error');
  rows.delete(key(routeId,previousOrder,calc));
  rows.set(key(routeId,targetOrder,calc),data);
  closeModal(); setStatus(`${LABELS[calc]||calc}: ✓ W automacie. Termin: ${mode==='last_day'?'ostatni dzień miesiąca':`${day}. dzień miesiąca`}.`,'ok'); scheduleSync(0);
}

async function disableModal(){
  if(!pending||!modal)return; const {calc,order,rowSectionOrder}=pending; const routeId=state.currentRoute?.id; if(!routeId)return; modal.disable.disabled=true;
  const storedOrder=Number(rowSectionOrder||order);
  const {data,error}=await supabase.from(TABLE).update({enabled:false,processing_since:null}).eq('organization_id',state.organizationId).eq('route_id',routeId).eq('section_order',storedOrder).eq('calculator_key',calc).select('*').single(); modal.disable.disabled=false;
  if(error)return setStatus(`Nie udało się wyłączyć automatu: ${error.message}`,'error'); rows.set(key(routeId,storedOrder,calc),data); closeModal(); setStatus(`${LABELS[calc]||calc}: automat miesięczny wyłączony.`,'ok'); scheduleSync(0);
}

async function loadRowsSnapshot(routeId){
  const snapshot=new Map();
  if(!supabase||!state.organizationId||!routeId)return snapshot;

  const organizationId=state.organizationId;
  const {data,error}=await supabase
    .from(TABLE)
    .select('*')
    .eq('organization_id',organizationId)
    .eq('route_id',routeId);

  if(error){
    console.warn('[D24 MONTHLY UI]',error);
    return null;
  }

  for(const row of data||[]){
    snapshot.set(key(routeId,row.section_order,row.calculator_key),row);
  }
  return snapshot;
}

function replaceRows(snapshot){
  if(!(snapshot instanceof Map))return;
  rows.clear();
  for(const [rowKey,row] of snapshot)rows.set(rowKey,row);
}

function automationRowForSection(routeId, sectionOrderValue, calc) {
  const exact = rows.get(key(routeId, sectionOrderValue, calc));
  if (exact?.enabled) return exact;

  const candidates = [...rows.values()].filter(row => (
    String(row?.route_id || '') === String(routeId || '')
    && String(row?.calculator_key || '') === String(calc || '')
  ));
  const enabled = candidates.filter(row => row?.enabled);

  // Po zapisaniu nowej wersji section_order może się zmienić. Jeżeli dla
  // danego kalkulatora istnieje dokładnie jeden aktywny automat, możemy go
  // bezpiecznie rozpoznać bez zgadywania po numerze starej sekcji.
  if (enabled.length === 1) return enabled[0];
  if (exact) return exact;
  return candidates.length === 1 ? candidates[0] : null;
}

function applyMonthlyButtonState(button,{enabled,routeId,limitError,row}){
  button.disabled=!routeId||Boolean(limitError);
  button.dataset.enabled=enabled?'true':'false';
  button.textContent=enabled?ACTIVE_TEXT:INACTIVE_TEXT;
  button.className=`button small automation-toggle ${enabled?'is-active':'is-inactive'} ${BUTTON_CLASS}`;
  button.setAttribute('aria-pressed',enabled?'true':'false');
  button.title=limitError||(!routeId
    ?'Najpierw zapisz linię'
    :enabled
      ?`Automat miesięczny aktywny. Następny termin: ${row?.next_run_on||'—'}. Kliknij, aby zmienić termin lub wyłączyć.`
      :'Kliknij, aby dodać ten odcinek do automatu miesięcznego.');
}

async function syncButtonsOnce(){
  const routeId=String(state.currentRoute?.id||'');
  const organizationId=String(state.organizationId||'');

  const snapshot=await loadRowsSnapshot(routeId);

  // Jeśli w trakcie zapytania użytkownik przełączył linię, wynik starego
  // zapytania nie może zmienić przycisków nowej linii.
  if(
    routeId!==String(state.currentRoute?.id||'')
    || organizationId!==String(state.organizationId||'')
  )return;

  // Błąd pobrania nie kasuje ostatniego poprawnego stanu UI.
  if(snapshot instanceof Map)replaceRows(snapshot);

  const cards=[...document.querySelectorAll('#sectionsList .section-card')];
  for(const card of cards){
    const calc=String(card.dataset.calculator||'');
    if(!SUPPORTED.has(calc))continue;

    const actions=card.querySelector('.section-actions');
    if(!actions)continue;

    const order=sectionOrder(card);
    if(!order)continue;

    const section=sectionFor(order,calc);
    let button=actions.querySelector(`.${BUTTON_CLASS}`);

    if(!button){
      button=document.createElement('button');
      button.type='button';
      button.className=`button small automation-toggle is-inactive ${BUTTON_CLASS}`;
      button.addEventListener('click',()=>{
        openModal(sectionFor(order,calc),calc,order)
          .catch(error=>setStatus(error.message,'error'));
      });
      actions.appendChild(button);
    }

    const row=automationRowForSection(routeId,order,calc);
    const limitError=validateSection(section,calc);
    const enabled=Boolean(row?.enabled);

    applyMonthlyButtonState(button,{
      enabled,
      routeId,
      limitError,
      row,
    });
  }
}

async function syncButtons(){
  if(syncRunning){
    syncAgain=true;
    return;
  }

  syncRunning=true;
  try{
    do{
      syncAgain=false;
      await syncButtonsOnce();
    }while(syncAgain);
  }finally{
    syncRunning=false;
  }
}

function scheduleSync(delay=20){
  clearTimeout(timer);
  timer=setTimeout(()=>{
    syncButtons().catch(error=>console.warn('[D24 MONTHLY UI]',error));
  },delay);
}

function mutationTouchesSectionCards(mutation){
  const nodes=[...(mutation.addedNodes||[]),...(mutation.removedNodes||[])];
  return nodes.some(node=>{
    if(node?.nodeType!==1)return false;
    const element=node;
    return Boolean(
      element.matches?.('.section-card')
      || element.querySelector?.('.section-card')
      || element.id==='sectionsList'
    );
  });
}

export function initBrowserlessMonthlyUi(){
  if(observer)return;

  // Obserwujemy wyłącznie kontener kart. Zmiana tekstu własnego przycisku
  // "✓ W automacie" NIE może ponownie uruchamiać synchronizacji.
  const sectionsRoot=document.getElementById('sectionsList');
  if(sectionsRoot){
    observer=new MutationObserver(mutations=>{
      if(mutations.some(mutationTouchesSectionCards))scheduleSync(0);
    });
    observer.observe(sectionsRoot,{subtree:true,childList:true});
  }else{
    observer=new MutationObserver(()=>scheduleSync(0));
    observer.observe(document.documentElement,{subtree:true,childList:true});
  }

  window.addEventListener('focus',()=>scheduleSync(0));
  window.addEventListener('d24:calculation-saved',()=>scheduleSync(0));
  document.addEventListener('visibilitychange',()=>{
    if(!document.hidden)scheduleSync(0);
  });

  scheduleSync(0);
}

